'use client';

import { useMemo, useState } from 'react';

import { AttachmentLightbox, type LightboxAttachment } from '../../components/attachment-lightbox';
import { useArticleImages } from './article-images-context';

interface ArticleImageProps {
	src: string;
	alt: string;
	/** Visible caption under the image — mandatory for docs (enforced by build-docs.mjs). */
	caption?: string;
}

const EXTENSION_MIME: Record<string, string> = {
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	gif: 'image/gif',
	webp: 'image/webp',
	avif: 'image/avif',
	svg: 'image/svg+xml',
};

const imageMimeFromUrl = (url: string): string => {
	const ext = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase() ?? '';
	return EXTENSION_MIME[ext] ?? 'image/png';
};

const toLightboxAttachment = (image: {
	src: string;
	alt: string;
	caption?: string;
}): LightboxAttachment => ({
	id: image.src,
	fileName: image.src.split(/[?#]/)[0].split('/').pop() ?? 'image',
	fileType: imageMimeFromUrl(image.src),
	fileSize: 0,
	url: image.src,
	caption: image.caption ?? image.alt,
});

/**
 * ArticleImage — figure with a mandatory caption used by the article markdown
 * renderer (docs + blog). The frame carries a faint surface fill so images with
 * transparent backgrounds stay legible, and clicking opens the shared
 * AttachmentLightbox (zoom/pan/download).
 *
 * Note: this intentionally uses a raw `<button>` as the image frame — the DS
 * Button variants are control-shaped and can't wrap full-bleed media.
 */
export const ArticleImage = ({ src, alt, caption }: ArticleImageProps): React.JSX.Element => {
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const captionText = caption ?? alt;
	const articleImages = useArticleImages();

	// All of the article's images so the lightbox can arrow-key between them;
	// outside a provider (or if this src isn't in it) fall back to just this one.
	const { attachments, index } = useMemo(() => {
		const ownIndex = articleImages.findIndex((image) => image.src === src);
		if (ownIndex === -1) {
			return { attachments: [toLightboxAttachment({ src, alt, caption })], index: 0 };
		}
		return { attachments: articleImages.map(toLightboxAttachment), index: ownIndex };
	}, [articleImages, src, alt, caption]);

	return (
		<figure className="my-6">
			<button
				type="button"
				onClick={() => setLightboxOpen(true)}
				aria-label={`Enlarge image: ${captionText}`}
				className="group block w-full cursor-zoom-in overflow-hidden rounded-lg bg-neutral/[0.03] transition-colors duration-200 hover:bg-neutral/[0.04] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
			>
				<img
					src={src}
					alt={alt}
					draggable={false}
					className="block w-full transition-transform duration-300 ease-out group-hover:scale-[1.015]"
				/>
			</button>
			<figcaption className="mt-2.5 text-center text-[13px] leading-[1.5] text-neutral/40">
				{captionText}
			</figcaption>
			{lightboxOpen && (
				<AttachmentLightbox
					attachments={attachments}
					initialIndex={index}
					onClose={() => setLightboxOpen(false)}
				/>
			)}
		</figure>
	);
};
